import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Change-order surface — T-0102 / E06-F02 (T-0064 backend).
 *
 * Read endpoint:
 *   - getChangeOrder(sowId) → GET /api/v1/contract/change-orders/:sow_id
 *     Returns the SOW head metadata (status / effective_date / version_seq),
 *     the linked supersede-chain pointers (superseded_sow_id ←/→
 *     superseded_by_sow_id), and the list of deliverable_revisions applied
 *     against this SOW (added/modified/removed entries with their effective
 *     ranges and revised_payload diffs).
 *
 * The Changes screen uses this to render the supersede-chain SVG timeline
 * (one node per SOW version, edges following the superseded_by_sow_id
 * pointer) plus a per-change diff card listing the revisions that were
 * applied at each version.
 *
 * Backend gap: walking the full chain requires the screen to fetch each SOW
 * in the chain (start from the current SOW; follow superseded_sow_id back
 * one step at a time). v1 client-side walks the chain via N fetches with
 * react-query — when the screen complains about latency a follow-up ticket
 * can add an aggregate `/api/v1/engagements/:id/contract/chain` endpoint.
 */

export const DeliverableRevisionSchema = z.object({
  id: z.string().uuid(),
  register_entry_id: z.string().uuid(),
  sow_id: z.string().uuid(),
  change_type: z.string(),
  effective_start: z.string(),
  effective_end: z.string().nullable(),
  revised_payload: z.unknown(),
  override_record_id: z.string().uuid().nullable(),
  created_at: z.string(),
  deliverable_title: z.string().nullable().optional(),
  rationale: z.string().nullable().optional(),
});
export type DeliverableRevision = z.infer<typeof DeliverableRevisionSchema>;

export const ChangeOrderSchema = z.object({
  sow_id: z.string().uuid(),
  version_seq: z.number(),
  status: z.string(),
  effective_date: z.string(),
  superseded_sow_id: z.string().uuid().nullable(),
  superseded_by_sow_id: z.string().uuid().nullable(),
  revisions: z.array(DeliverableRevisionSchema),
});
export type ChangeOrder = z.infer<typeof ChangeOrderSchema>;

export function getChangeOrder(sowId: string): Promise<ChangeOrder> {
  return apiClient(
    `/api/v1/contract/change-orders/${encodeURIComponent(sowId)}`,
    ChangeOrderSchema,
  );
}

export function useChangeOrderQuery(sowId: string | undefined) {
  return useQuery({
    queryKey: ['change-order', sowId] as const,
    queryFn: () => getChangeOrder(sowId!),
    enabled: Boolean(sowId),
  });
}

// ===========================================================================
// Change-order registration (Tier 2). Registers an amended SOW document as
// the new chain head and queues the AI diff-extraction run — the extracted
// revisions land in the review queue like any other proposal.
// ===========================================================================

const RegisterChangeOrderResultSchema = z
  .object({
    sow_id: z.string(),
    superseded_sow_id: z.string().nullable().optional(),
    extraction_run_queued: z.boolean().optional(),
  })
  .passthrough();

export interface RegisterChangeOrderBody {
  engagement_id: string;
  document_id: string;
  effective_date: string;
}

export function useRegisterChangeOrderMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: RegisterChangeOrderBody;
      idempotencyKey?: string;
    }) =>
      apiClient(`/api/v1/contract/change-orders`, RegisterChangeOrderResultSchema, {
        method: 'POST',
        body,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    onSuccess: () => {
      // `['change-orders', engagementId]` = the chain list; `['change-order',
      // sowId]` = one version. The original code invalidated a key no query
      // used, so the screen never refreshed after registering.
      void qc.invalidateQueries({ queryKey: ['change-orders', engagementId] });
      void qc.invalidateQueries({ queryKey: ['change-order'] });
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
    },
  });
}

// ===========================================================================
// Undo a registration — DELETE /api/v1/contract/change-orders/:sow_id
// (C2-51). Allowed for the chain head only, while no revisions have been
// applied against it; the prior version becomes current again. The server
// refuses anything else with a 409 that names why.
// ===========================================================================

const RemoveChangeOrderVersionResultSchema = z
  .object({
    removed_sow_id: z.string(),
    current_sow_id: z.string(),
    document_id: z.string(),
  })
  .passthrough();
export type RemoveChangeOrderVersionResult = z.infer<typeof RemoveChangeOrderVersionResultSchema>;

export function useRemoveChangeOrderVersionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sowId }: { sowId: string }) =>
      apiClient(
        `/api/v1/contract/change-orders/${encodeURIComponent(sowId)}`,
        RemoveChangeOrderVersionResultSchema,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['change-orders', engagementId] });
      void qc.invalidateQueries({ queryKey: ['change-order'] });
      void qc.invalidateQueries({ queryKey: ['edl', engagementId] });
    },
  });
}

// ===========================================================================
// Chain list — GET /api/v1/contract/engagements/:id/change-orders
// ===========================================================================

const SowHeadSchema = z.object({
  sow_id: z.string().uuid(),
  version_seq: z.number(),
  status: z.string(),
  effective_date: z.string(),
  document_id: z.string().uuid(),
  document_title: z.string(),
  registered_at: z.string(),
  revision_counts: z.object({ added: z.number(), modified: z.number(), removed: z.number() }),
});
export type SowHead = z.infer<typeof SowHeadSchema>;

const DetectedChangeOrderSchema = z.object({
  document_id: z.string().uuid(),
  document_title: z.string(),
  uploaded_at: z.string(),
  classification_confidence: z.number(),
  ready_to_register: z.boolean(),
  // The AI's pending reading of the document; null until analysed (or once decided).
  analysis: z
    .object({
      ai_proposal_id: z.string().uuid(),
      summary: z.string().nullable(),
      effective_date: z.string().nullable(),
      revision_counts: z.object({ added: z.number(), modified: z.number(), removed: z.number() }),
    })
    .nullable()
    .optional(),
});
export type DetectedChangeOrder = z.infer<typeof DetectedChangeOrderSchema>;

export const ChangeOrderChainSchema = z.object({
  baseline: SowHeadSchema.nullable(),
  baseline_candidate: z
    .object({ document_id: z.string().uuid(), document_title: z.string() })
    .nullable(),
  change_orders: z.array(SowHeadSchema),
  detected: z.array(DetectedChangeOrderSchema),
});
export type ChangeOrderChain = z.infer<typeof ChangeOrderChainSchema>;

/**
 * The engagement's whole SOW chain plus change-order documents the
 * classifier detected but nobody has registered yet. This is what lets the
 * Change orders tab render without a `?sow=` seed and surface an uploaded
 * change order on its own.
 */
export function useChangeOrderChainQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['change-orders', engagementId] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/contract/engagements/${encodeURIComponent(engagementId!)}/change-orders`,
        ChangeOrderChainSchema,
      ),
    enabled: Boolean(engagementId),
  });
}
